import React from 'react';
import './FacelessVideoFactory.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

const VideoReviewCard = ({ video, onRefresh }) => {
  
  const handleApprove = async () => {
    try {
      await fetch(`${API_BASE}/videos/${video.id}/approve`, { method: 'POST' });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to approve video:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to remove this video from the queue?")) return;
    try {
      await fetch(`${API_BASE}/videos/${video.id}`, { method: 'DELETE' });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to delete video:', error);
    }
  };

  const getStatusColor = (status) => {
    if (status === "Scheduled") return "rgba(121, 40, 202, 0.2)";
    if (status === "Publishing...") return "rgba(255, 0, 127, 0.2)";
    if (status === "Published") return "rgba(16, 185, 129, 0.2)";
    if (status === "Publishing Failed") return "rgba(239, 68, 68, 0.2)";
    return "rgba(255,193,7,0.2)";
  };

  const getStatusTextColor = (status) => {
    if (status === "Scheduled") return "#d1b3ff";
    if (status === "Publishing...") return "#ff007f";
    if (status === "Published") return "#10b981";
    if (status === "Publishing Failed") return "#ef4444";
    return "#ffc107";
  };

  return (
    <div className="fvf-glass-card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <span style={{ 
          background: getStatusColor(video.status), 
          color: getStatusTextColor(video.status), 
          padding: '4px 8px', 
          borderRadius: '12px', 
          fontSize: '0.75rem', 
          fontWeight: 'bold' 
        }}>
          {video.status}
        </span>
        <span style={{ color: '#8b949e', fontSize: '0.875rem' }}>{video.platform}</span>
      </div>

      <div style={{ 
        width: '100%', 
        height: '400px', 
        borderRadius: '8px', 
        overflow: 'hidden', 
        marginBottom: '16px',
        position: 'relative',
        background: '#000'
      }}>
        {video.videoUrl && (video.videoUrl.includes('.mp4') || video.videoUrl.includes('video')) ? (
          <video 
            key={video.videoUrl}
            src={video.videoUrl} 
            poster={video.thumbnailUrl && !video.thumbnailUrl.includes('.mp4') ? video.thumbnailUrl : undefined}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            controls
            playsInline
            onError={(e) => {
              // If video fails to load (e.g. CORS), show thumbnail fallback
              const parent = e.target.parentNode;
              e.target.style.display = 'none';
              const img = document.createElement('img');
              img.src = video.thumbnailUrl || video.videoUrl;
              img.style.cssText = 'width:100%;height:100%;object-fit:cover';
              img.alt = video.title;
              parent.insertBefore(img, e.target);
            }}
          />
        ) : video.thumbnailUrl ? (
          <>
            <img 
              src={video.thumbnailUrl} 
              alt={video.title} 
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: video.status === 'Published' ? 0.7 : 1 }} 
            />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '48px',
              height: '48px',
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}>
              <span style={{ color: 'white', marginLeft: '4px' }}>▶</span>
            </div>
          </>
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>
            No preview available
          </div>
        )}


        {/* Dynamic Text Overlay (Captions Simulation) */}
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          right: '10%',
          textAlign: 'center',
          pointerEvents: 'none',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
          zIndex: 5
        }}>
          <h2 style={{ 
            color: 'white', 
            fontSize: '1.5rem', 
            fontWeight: '900', 
            textTransform: 'uppercase',
            lineHeight: '1.2'
          }}>
            {video.hook}
          </h2>
        </div>

        {/* Download Link */}
        <a 
          href={video.videoUrl} 
          download={`${video.title.replace(/\s+/g, '_')}.mp4`}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'rgba(0,0,0,0.6)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            textDecoration: 'none',
            zIndex: 10,
            border: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          ⬇️ Download
        </a>

        {video.audioPreviewStr && !video.videoUrl.includes('localhost') && (
          <div style={{ position: 'absolute', bottom: '10px', left: '10px', right: '10px', background: 'rgba(0,0,0,0.7)', borderRadius: '8px', padding: '8px' }}>
            <span style={{ color: 'white', fontSize: '10px', display: 'block', marginBottom: '4px' }}>AI Voiceover Preview:</span>
            <audio controls src={video.audioPreviewStr} style={{ width: '100%', height: '30px' }} />
          </div>
        )}
      </div>

      <h3 style={{ color: 'white', marginTop: 0, marginBottom: '8px', fontSize: '1.25rem', opacity: video.status === 'Published' ? 0.5 : 1 }}>
        {video.title}
      </h3>
      
      <p style={{ color: '#a3a3a3', fontSize: '0.875rem', marginBottom: '8px', opacity: video.status === 'Published' ? 0.5 : 1 }}>
        <strong>Hook:</strong> "{video.hook}"
      </p>

      {video.script && (
        <div style={{ 
          background: 'rgba(255,255,255,0.03)', 
          padding: '12px', 
          borderRadius: '8px', 
          fontSize: '0.8rem', 
          color: '#8b949e',
          marginBottom: '16px',
          maxHeight: '100px',
          overflowY: 'auto',
          borderLeft: '2px solid #30363d'
        }}>
          <strong>Full Script:</strong><br/>
          {video.script}
        </div>
      )}
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
        {video.hashtags.map((tag, idx) => (
          <span key={idx} style={{ 
            color: '#7928ca', 
            background: 'rgba(121,40,202,0.1)', 
            padding: '2px 8px', 
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            {tag}
          </span>
        ))}
      </div>

      {video.status === "Published" && video.publishDetails && (
        <div style={{ marginBottom: '16px', borderTop: '1px solid #30363d', paddingTop: '12px' }}>
          <p style={{ fontSize: '0.75rem', color: '#8b949e', marginBottom: '8px' }}>🚀 Published to:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {video.publishDetails.map((detail, idx) => (
              <a 
                key={idx} 
                href={detail.url} 
                target="_blank" 
                rel="noreferrer"
                style={{ color: '#58a6ff', fontSize: '0.8rem', textDecoration: 'none' }}
              >
                🔗 {detail.platform}
              </a>
            ))}
          </div>
        </div>
      )}

      {video.status === "Awaiting Review" && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: 'auto' }}>
          <button onClick={handleDelete} className="fvf-btn fvf-btn-outline" style={{ borderColor: '#ef4444', color: '#ef4444' }}>
            Reject
          </button>
          <button onClick={handleApprove} className="fvf-btn fvf-btn-primary" title="Publish to YouTube, TikTok & Instagram">
            <span>Approve</span>
            <span style={{ marginLeft: '8px', letterSpacing: '2px' }}>📺🎵📸</span>
          </button>
        </div>
      )}

    </div>
  );
};

export default VideoReviewCard;
